import { AgNumberValueCellEditingWithLoaderRenderer } from 'components/AgGrid/AgNumberValueCellEditingWithLoaderRenderer';
import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderChangePriceItem } from 'gql/graphql';
import { WorkOrderStatusEnum, WorkInSumEnum } from 'gql/graphql';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { useUerSetWorkOrderChangeWorkRatio } from '../hooks/useUerSetWorkOrderChangeWorkRatio';

interface IWorkRatioEditCellRendererComponent extends ICellRendererParams<WorkOrderChangePriceItem> {
    workOrderStatus: WorkOrderStatusEnum;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
    onSuccess?: () => void;
}

export const WorkRatioEditCellRendererComponent = (props: IWorkRatioEditCellRendererComponent) => {
    const { workOrderStatus, useAgCellRowDataRefreshParams, useAgCellRowDataRefresh, data, api, onSuccess, ...rest } =
        props;

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            api,
            requestParams: {
                isSMRItem: true,
                ...useAgCellRowDataRefreshParams,
            },
        }) ?? {};

    const onSuccessLocal = useCallback(() => {
        if (data?.workOrderChangePriceId) {
            reloadHandler?.([data?.itemId]);
        } else {
            api?.refreshInfiniteCache();
        }

        onSuccess?.();
    }, [api, data?.itemId, data?.workOrderChangePriceId, onSuccess, reloadHandler]);

    const isEditable = useMemo(() => {
        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return data?.workInSum === WorkInSumEnum.ParticipateWithRatio;
        }

        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            return data?.isActKC2Price;
        }

        return false;
    }, [data?.isActKC2Price, data?.workInSum, workOrderStatus]);

    const { uerSetWorkOrderChangeWorkRatioMakeRequest, uerSetWorkOrderChangeWorkRatioStatus } =
        useUerSetWorkOrderChangeWorkRatio({
            workOrderPriceId: data?.workOrderPriceId,
            workOrderChangePriceId: data?.workOrderChangePriceId,
            onSuccess: onSuccessLocal,
        });

    const onChange = useCallback(
        (value) => {
            uerSetWorkOrderChangeWorkRatioMakeRequest(value);
        },
        [uerSetWorkOrderChangeWorkRatioMakeRequest],
    );

    return (
        <AgNumberValueCellEditingWithLoaderRenderer
            decimalScale={4}
            isEditable={!!isEditable}
            idKey="id"
            columnKey="workRatio"
            debounceTime={500}
            onChange={onChange}
            isShowLoader={uerSetWorkOrderChangeWorkRatioStatus === RequestStatus.FETCHING}
            data={data}
            isNullPossible
            size="xs"
            api={api}
            {...rest}
        />
    );
};

WorkRatioEditCellRendererComponent.displayName = 'WorkRatioEditCellRendererComponent';
